import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep';
import { Loader, SimpleModal, CustomDelete, MinLoader, LocalDateRow } from 'components';
import { Colors, FindLoad, useModal } from 'utils';
import { formActions, httpRequestsOnSuccessActions } from 'store';
import { ensureFormIds } from 'utils/ensureFormIds';
import { documentVersionService } from 'store/documentVersion/documentVersion.service';
import { FORM_SETTINGS, SINGLE_FORM, DISCARD_ACTION_TYPE, AI_FORM_GENERATED_TOAST } from './constants';
import { normalizeFormIds } from './utils/normalizeFormIds';
import {
    FormDetailHeader,
    Properties,
    FormBody,
    FormSections,
    Attributes,
    formTabEnums,
    FormBuilderProvider,
    useFormBuilder,
} from './formDetail';
import { FormPreview } from './formPreview';
import { AiGenerationModal } from './fragments/aiGenerationModal';
import { formService } from 'store/form/form.service';


const CreateFormContent = ({}) => {
    const { setFormList, setFormListRaw, resetHistory, formList, isDirty, setIsDirty, previewing, setPreviewing, initialFormListStr, setImageUrls, setActiveSection } = useFormBuilder();
    const previewBackupRef = useRef(null);
    const form = useSelector((state) => state.forms.form);
    const params = useParams();
    const loader = FindLoad(FORM_SETTINGS);
    const loadForm = FindLoad(SINGLE_FORM);
    const discardChanges = FindLoad(DISCARD_ACTION_TYPE);
    const dispatch = useDispatch();
    const location = useLocation();
    const info = location?.state;
    const { open, close } = useModal();
    const navigate = useNavigate();
    const [draftInfo, setDraftInfo] = useState(null);
    const draftState = useSelector((state) => state.documentVersion.draftState);

    // Live mirror of formList so the post-load "settle" re-baseline (below) can
    // read the CURRENT state from inside a requestAnimationFrame closure without
    // re-subscribing. Genuine user edits are detected separately (see below).
    const formListRef = useRef(formList);
    formListRef.current = formList;
    // Bumped on every (form,id) load. The settle callback captures the value at
    // schedule time and no-ops if a newer load superseded it.
    const loadGenRef = useRef(0);
    // Set true while a server DRAFT is overlaid on formList — the settle
    // re-baseline must NOT absorb draft deltas into the baseline (a draft is
    // genuine unsaved work and must keep reading dirty / show its banner).
    const draftAppliedRef = useRef(false);
    // True once THIS load's own draft fetch has resolved (draft or not, or failed).
    // The reopen-unsaved-work overlay happens only inside that fetch, exactly once
    // per load; until it resolves the [draftState] effect ignores the store, so a
    // value left from another template never shows its banner here. Reset on every load.
    const draftLoadedRef = useRef(false);

    useEffect(() => {
        dispatch(formActions.getFormSettings());
        if (params?.id) {
            dispatch(formActions.getFormById(params?.id));
        } else {
            dispatch(formActions.resetFormById());
        }
    }, [dispatch, params?.id]);
    // Handle duplicate: populate formList from location.state.duplicateData
    useEffect(() => {
        const duplicateData = location?.state?.duplicateData;
        if (duplicateData && !params?.id) {
            const formParams = cloneDeep(duplicateData);
            formParams.models = duplicateData?.models?.[0];
            formParams.model = duplicateData?.models?.[0];
            // Normalize ids up front so the lazy per-section/per-attribute
            // normalizers don't fire post-mount and false-trigger the dirty check.
            setFormListRaw(normalizeFormIds(formParams));
            resetHistory();
            // Clear the state so refresh doesn't re-duplicate
            window.history.replaceState({}, document.title);
        }
    }, []);

    useEffect(() => {
        if (form?.id && params?.id) {
            // Ensure every section/attribute/option/table-column carries a uuid so
            // version history can match by id everywhere. ensureFormIds already
            // covers what the lazy formSections/formBody normalizers backfill, so
            // capturing the baseline from this object keeps their `.some(!id)`
            // guards satisfied — they never fire and never race the baseline.
            const formParams = ensureFormIds(cloneDeep(form));
            formParams.models = form?.models?.[0];
            formParams.model = form?.models?.[0];
            // imageUrls belongs in the imageUrls CONTEXT, never in formList: signed
            // URLs are re-minted on every read, so keeping them in formList would
            // false-positive the dirty check after every save round-trip. Strip
            // once here and push the SAME object into both state and the baseline
            // so they are byte-identical on load.
            const { imageUrls, ...formParamsWithoutUrls } = formParams;
            setImageUrls(imageUrls || {});
            setFormListRaw(formParamsWithoutUrls);
            initialFormListStr.current = JSON.stringify(formParamsWithoutUrls);
            draftAppliedRef.current = false;
            draftLoadedRef.current = false;
            setIsDirty(false);
            resetHistory();

            // Re-anchor the baseline AFTER the load has fully settled (two frames).
            // The saved-vs-baseline comparison is a single-instant snapshot, but a
            // freshly-mounted builder can emit one benign programmatic write while
            // settling (id backfill, rich-text re-serialization, etc.) that lands
            // AFTER this synchronous capture — on a cold page that leaves the form
            // reading dirty with no user edit (a warm cache masked it by re-running
            // this effect). Re-capturing from the settled formList makes cold and
            // warm behave identically. Guarded so it never absorbs a real draft or
            // a superseding load.
            const gen = ++loadGenRef.current;
            const settleBaseline = () => {
                if (loadGenRef.current !== gen) return; // a newer load superseded us
                if (draftAppliedRef.current) return; // draft overlay must stay dirty
                initialFormListStr.current = JSON.stringify(formListRef.current);
                setIsDirty(false);
            };
            const raf1 = requestAnimationFrame(() => requestAnimationFrame(settleBaseline));

            // Reopen unsaved work: this load's own fetch overlays the draft's deltas
            // once (main stays untouched until Save). Fetched here, not through the
            // Redux draftState: a load with no draft leaves the store at `null`, the
            // same value it already held, so an effect on it never ran and the first
            // later capture refresh was taken for the reopen — it reset formList to
            // saved + captured ADDs, and the elements deleted since the last save (a
            // REMOVE is only recorded on save) came back.
            documentVersionService
                .getDraftState({ resourceModel: 'FORM', resourceId: params.id })
                .then((res) => {
                    if (loadGenRef.current !== gen) return; // a newer load superseded us
                    const draft = res?.data?.data || res?.data;
                    if (draft?.hasDraft && draft?.state) {
                        // Mark BEFORE the state write so the settle re-baseline (which may run
                        // on a later frame) sees the draft and refuses to absorb it.
                        draftAppliedRef.current = true;
                        setFormListRaw(ensureFormIds({ ...formParamsWithoutUrls, ...draft.state }));
                        setDraftInfo({ changeCount: draft.changeCount });
                    } else {
                        setDraftInfo(null);
                    }
                })
                .catch(() => {
                    /* versioning must never block editing */
                })
                .finally(() => {
                    if (loadGenRef.current === gen) draftLoadedRef.current = true;
                });

            return () => cancelAnimationFrame(raf1);
        }
    }, [form, params?.id]);

    // ── AI generation ──────────────────────────────────────────────────────
    // The create modal uploads the PDF, creates the template, then navigates
    // here carrying the S3 key. Starting the run from the builder rather than
    // from create keeps create's contract unchanged and means a generation that
    // fails leaves a real, editable template behind instead of nothing.
    const aiStartedRef = useRef(false);
    // sectionId → its position in the outline, so arrivals can be ordered.
    const aiSectionOrderRef = useRef(new Map());

    useEffect(() => {
        const request = location?.state?.aiGenerate;
        // Either input is enough — a document to transcribe, a description to
        // build from, or both.
        if ((!request?.fileKey && !request?.prompt) || !params?.id || aiStartedRef.current) return;
        aiStartedRef.current = true;

        window.history.replaceState({}, document.title);

        formService
            .startAiGenerationService(params.id, { fileKey: request.fileKey, prompt: request.prompt })
            .then(({ data }) => {
                open(
                    <AiGenerationModal
                        formId={params.id}
                        conversationId={data?.conversationId}
                        onSection={appendGeneratedSection}
                        onComplete={() => {
                            dispatch(formActions.getFormById(params.id));
                            dispatch(httpRequestsOnSuccessActions.appendSuccess(AI_FORM_GENERATED_TOAST));
                        }}
                    />,
                    {
                        styles: {
                            wrapperStyle: { width: 384, maxWidth: '100%' },
                            headerStyle: { display: 'none' },
                            // bodyStyle: { padding: '0 32px 32px' },
                        },
                    },
                );
            })
            .catch(() => {});
    }, [params?.id]);

    /**
     * Splice a finished section into the builder behind the modal.
     *
     * Written with `setFormListRaw` and followed by a baseline re-anchor on
     * purpose: these are the server's own writes echoed back, not the author's
     * edits. Routing them through `setFormList` would fill the undo stack with
     * machine changes and leave the template reading dirty the moment
     * generation ends, prompting to save work that is already saved.
     */
    const appendGeneratedSection = (section, index) => {
        aiSectionOrderRef.current.set(section?.id, index);

        setFormListRaw((prev) => {
            const sections = prev?.sections || [];
            if (sections.some((existing) => existing?.id === section?.id)) return prev;

            // Place by the outline's index rather than appending. The section
            // passes run in parallel and arrive in whatever order they finish,
            // so appending would show the document's sections shuffled — and
            // the author would watch them land in the wrong places. Slot it in
            // before the first already-placed AI section that belongs after it;
            // sections that existed before the run keep their positions.
            const at = sections.findIndex((existing) => {
                const placed = aiSectionOrderRef.current.get(existing?.id);
                return placed !== undefined && placed > index;
            });

            const nextSections = [...sections];
            nextSections.splice(at === -1 ? nextSections.length : at, 0, section);

            const next = { ...prev, sections: nextSections };
            initialFormListStr.current = JSON.stringify(next);
            return next;
        });
        setIsDirty(false);
    };

    const handleDiscardDraft = () => {
        if (!params?.id) return;
        documentVersionService
            .discardDraft({ resourceModel: 'FORM', resourceId: params.id })
            .catch(() => {})
            .finally(() => {
                const saved = initialFormListStr.current ? JSON.parse(initialFormListStr.current) : {};
                setFormListRaw(saved);
                setIsDirty(false);
                setDraftInfo(null);
            });
    };

    // draftState in the store is refreshed by every captureDocumentChanges (and by the
    // version history panel). Its ONLY job here is the banner count: it never touches
    // formList, or it would reset the form to (saved + captured deltas) and wipe live
    // edits the round-trip has not echoed yet — a section created this session, or
    // elements deleted since the last save. The reopen overlay is done once by the
    // load's own fetch (above).
    useEffect(() => {
        if (!form?.id || !params?.id || !draftLoadedRef.current) return;
        setDraftInfo(draftState?.hasDraft ? { changeCount: draftState.changeCount } : null);
    }, [draftState]);


    // Read-only preview of a historical version: stash the live form, swap in the
    // reconstructed state, and flag previewing (capture is paused while set).
    const handlePreview = (state, version) => {
        if (!previewBackupRef.current) previewBackupRef.current = formList;
        setFormListRaw({ ...formList, ...state });
        // Land on the first section: the previewed version can have a different
        // section set than the live form, so a stale `activeSection` index would
        // point at the wrong (or a non-existent) section. Also clears the selected
        // attribute / Header-Footer view (side effect of setActiveSection).
        setActiveSection(1);
        setPreviewing({ versionNumber: version?.versionNumber, savedAt: version?.savedAt });
    };

    const handleStopPreview = () => {
        if (previewBackupRef.current) setFormListRaw(previewBackupRef.current);
        previewBackupRef.current = null;
        setPreviewing(null);
    };

    // Applied when a previous version is restored: the form jumps to that state,
    // which becomes the new saved baseline (the restore is itself a new version).
    const handleRestored = (state) => {
        const merged = { ...formList, ...state };
        previewBackupRef.current = null;
        setPreviewing(null);
        setFormListRaw(merged);
        // Restored version becomes the new baseline — reset to the first section
        // so the view isn't stuck on a stale/out-of-range section index.
        setActiveSection(1);
        initialFormListStr.current = JSON.stringify(merged);
        setIsDirty(false);
        resetHistory?.();
    };

    useEffect(() => {
        if (!params?.id && formList && !initialFormListStr.current) {
            initialFormListStr.current = JSON.stringify(formList);
        }
    }, [formList, params?.id]);

    useEffect(() => {
        if (initialFormListStr.current && formList) {
            setIsDirty(JSON.stringify(formList) !== initialFormListStr.current);
        }
    }, [formList]);

    // Removed trapPushed and prevDirty

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isDirty) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes. Are you sure you want to leave without saving?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    useEffect(() => {
        let isTrapActive = false;

        const handlePopState = (event) => {
            if (isDirty) {
                open(
                    <CustomDelete
                        info="Unsaved Changes"
                        text="You have unsaved changes. Are you sure you want to leave this page?"
                        innerText="Discard Changes"
                        color="blue"
                        handleDel={() => {
                            close();
                            setIsDirty(false);
                            setTimeout(() => {
                                window.history.back();
                            }, 50);
                        }}
                        handleClose={() => {
                            close();
                            window.history.pushState(null, '', window.location.href);
                        }}
                        noClose={true}
                    />
                );
            }
        };

        if (isDirty) {
            window.history.pushState(null, '', window.location.href);
            isTrapActive = true;
            window.addEventListener('popstate', handlePopState);
        }

        return () => {
            if (isTrapActive) {
                window.removeEventListener('popstate', handlePopState);
                window.history.back(); // Automatically pop the trap when tearing down (e.g., normal navigation)
            }
        };
    }, [isDirty]);

    useEffect(() => {
        const handleGlobalClick = (e) => {
            if (isDirty) {
                const link = e.target.closest('a');
                if (link && link.getAttribute('href') && !link.getAttribute('href').startsWith('http') && !link.getAttribute('target')) {
                    const href = link.getAttribute('href');

                    e.preventDefault();
                    e.stopPropagation();

                    open(
                        <CustomDelete
                            info="Unsaved Changes"
                            text="You have unsaved changes. Are you sure you want to leave this page?"
                            innerText="Discard Changes"
                            color="blue"
                            handleDel={() => {
                                close();
                                setIsDirty(false);
                                setTimeout(() => navigate(href), 50); // Use react-router navigate
                            }}
                            handleClose={() => close()}
                            noClose={true}
                        />
                    );
                }
            }
        };

        document.addEventListener('click', handleGlobalClick, true);

        return () => {
            document.removeEventListener('click', handleGlobalClick, true);
        };
    }, [isDirty]);


    if (loader?.length || loadForm?.length) {
        return <Loader />;
    }

    return (
        <div>
            {draftInfo && isDirty && (
                <div className="form-notice-banner form-notice-banner--draft">
                    <span>
                        Unsaved changes — {draftInfo.changeCount} edit{draftInfo.changeCount === 1 ? '' : 's'} not yet
                        saved to the template. Save to keep them in history.
                    </span>
                    <button onClick={handleDiscardDraft} className="form-notice-banner__btn">
                        Discard
                    </button>
                </div>
            )}

            {previewing && (
                <div className="form-notice-banner form-notice-banner--preview">
                    <span>
                        Read-only preview of version v{previewing.versionNumber}
                        {previewing.savedAt ? ' — ' : ''}
                        {previewing.savedAt ? <LocalDateRow date={previewing.savedAt} /> : null}
                        {'. '}
                        This is how the template looked at that point in time. Exit to return to editing.
                    </span>
                    <button onClick={handleStopPreview} className="form-notice-banner__btn">
                        Exit preview
                    </button>
                </div>
            )}
            <FormDetailHeader onPreview={handlePreview} onStopPreview={handleStopPreview} onRestored={handleRestored} />
            {/* A version preview renders the read-only FormPreview (same as the
                Preview tab), NOT the editable FormBody builder — the historical
                version must be viewable but never editable/deletable. */}
            {info?.activeTab === formTabEnums.PREVIEW || previewing ? (
                <FormPreview />
            ) : (
                <>
                    <div className="flex-align-start">
                        <FormSections />

                        <div className="flex-column full-width form-builder-body-col">
                            <FormBody />
                        </div>

                        <Properties />
                    </div>
                    <Attributes />
                </>
            )}
        </div>
    );
};

export const CreateForm = () => {
    return (
        <FormBuilderProvider>
            <CreateFormContent />
        </FormBuilderProvider>
    );
};
